import { Route, Routes, useNavigate, useParams } from "react-router";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import MetaInfoList from "components/MetaInfoList";
import SpacePath from "components/SpacePath";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tab from "ds/components/Tab";
import useErrorHandle from "hooks/useErrorHandle";
import useBlueprint from "shared/Blueprint/useBlueprint";
import { BlueprintState } from "types/generated";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import useCanManageBlueprint from "shared/Blueprint/access/useCanManageBlueprint";

import { FILTERS_ORDER_SETTINGS_KEY } from "../Blueprints/constants";
import { showCreateBlueprintDrawer } from "../Blueprints/CreateBlueprintDrawer";
import BlueprintFeatureGateCallout from "../Blueprints/FeatureGate/Callout";
import { showDeleteConfirmation } from "../Blueprints/DeleteConfirmation";
import BlueprintStateBadge from "../Blueprints/StateBadge";
import { showBlueprintDetailsDrawer } from "./DetailsDrawer";
import BlueprintIntegrations from "./Intergations";
import BlueprintTemplateBody from "./TemplateBody";
import BlueprintsFeatureGateTooltip from "../Blueprints/FeatureGate/Tooltip";
import { BlueprintActions } from "../Blueprints/FeatureGate/types";

const BlueprintPage = () => {
  const navigate = useNavigate();

  const { blueprintId } = useParams<{ blueprintId: string }>();

  const { error, loading, data } = useBlueprint(blueprintId);

  const handleOpenEditDrawer = () => {
    showCreateBlueprintDrawer({ blueprint: data?.blueprint });
  };

  const handleOpenCloneDrawer = () => {
    showCreateBlueprintDrawer({ blueprint: data?.blueprint, isCloneMode: true });
  };

  const handleOpenDetailsDrawer = () => {
    if (data?.blueprint) {
      showBlueprintDetailsDrawer({
        item: data.blueprint,
        ...(canManageBlueprint.result && { onEdit: handleOpenEditDrawer }),
      });
    }
  };

  useBreadcrumbs(
    [
      {
        title: "Blueprints",
        link: getBreadcrumbsBackUrl("/blueprints", FILTERS_ORDER_SETTINGS_KEY),
      },
      {
        title: data?.blueprint?.name || "",
      },
    ],
    [data?.blueprint?.name]
  );

  const canManageBlueprintFn = useCanManageBlueprint();

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.blueprint) {
    return <PageLoading />;
  }

  if (!data?.blueprint) {
    return <NotFoundPage />;
  }

  const blueprint = data.blueprint;
  const blueprintSchema = data.blueprintSchema;

  const handleDeleteClick = () => {
    showDeleteConfirmation({ name: blueprint.name, id: blueprint.id }).then(() =>
      navigate("/blueprints")
    );
  };

  const bluePrintUrl = `/blueprint/${data.blueprint.id}`;

  const isDraft = blueprint.state === BlueprintState.Draft;

  const canManageBlueprint = canManageBlueprintFn(blueprint);

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{blueprint.name}</ViewHeaderTitle>
            <BlueprintStateBadge state={blueprint.state} />
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
              Details
            </Button>

            <DropdownMenuEllipsis tooltip="Blueprint actions" dotsSize="medium">
              <BlueprintsFeatureGateTooltip
                on={(props) => (
                  <DropdownMenuItem {...props} onAction={handleOpenEditDrawer}>
                    Edit metadata
                  </DropdownMenuItem>
                )}
                blueprint={blueprint}
                action={BlueprintActions.Edit}
                tooltipPlacement="left"
              />

              <BlueprintsFeatureGateTooltip
                on={(props) => (
                  <DropdownMenuItem {...props} onAction={handleOpenCloneDrawer}>
                    Clone
                  </DropdownMenuItem>
                )}
                blueprint={blueprint}
                action={BlueprintActions.CreateBlueprint}
                tooltipPlacement="left"
              />

              {(canManageBlueprint.result || !!canManageBlueprint.message) && (
                <DropdownMenuItem
                  onAction={handleDeleteClick}
                  danger
                  isDisabled={!canManageBlueprint.result}
                  tooltip={canManageBlueprint.message}
                  tooltipPlacement="left"
                >
                  Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <SpacePath
              variant="meta"
              spaceId={blueprint.space.id}
              fallbackName={blueprint.space.name}
            />
            <TimestampMetaInfo timestamp={blueprint.updatedAt} />
          </MetaInfoList>
        </ViewHeaderScrollCollapse>

        <ViewHeaderNavigation>
          <Tab end to={bluePrintUrl} label="Template body" />
          <Tab to={`${bluePrintUrl}/integrations`} label="Integrations" />
        </ViewHeaderNavigation>
      </ViewHeader>
      <BlueprintFeatureGateCallout />
      <Routes>
        <Route
          index
          element={
            <BlueprintTemplateBody blueprint={blueprint} blueprintSchema={blueprintSchema} />
          }
        />

        <Route
          path="integrations"
          element={
            <BlueprintIntegrations
              blueprint={blueprint}
              space={blueprint.space}
              isDraft={isDraft}
            />
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

export default BlueprintPage;
